// src/auditoria/components/tx-simple-modal/tx-modal-guarda-saida.tsx
//
// Guarda de saída dos modais globais (`abrirModal`).
//
// O problema: o modal imperativo do projeto fecha no ESC e no clique fora sem
// avisar. Como boa parte dos ~120 pontos que abrem modal coloca um formulário
// fiscal longo lá dentro, esbarrar no ESC apagava tudo em silêncio.
//
// A regra combinada é "só atrapalha quem tem o que perder":
//   - modal sem campo nenhum (leitura, relatório, confirmação) fecha na hora,
//     exatamente como antes — este arquivo nunca chega a marcar sujeira;
//   - modal com formulário só pede confirmação se o usuário de fato mexeu em
//     algum campo. Formulário aberto e não tocado também fecha na hora.
//
// Como a sujeira é descoberta SEM alterar os pontos de chamada:
//
//   1) Sinal explícito (opcional). Qualquer componente dentro do modal pode
//      chamar `useTxModalGuardaSaida(formState.isDirty)` e passar a resposta do
//      React Hook Form. Quando existe pelo menos um sinal explícito registrado,
//      ele é a ÚNICA fonte da verdade — inclusive para dizer "não estou sujo"
//      depois de um `reset()`.
//
//   2) Heurística de DOM (padrão, vale para todo mundo sem tocar em nada).
//      A guarda observa a árvore do próprio modal:
//        - na primeira ação de verdade do usuário dentro do conteúdo
//          (`pointerdown`, `keydown` ou `paste`) tira uma foto do valor de
//          todos os campos. Congelar só nesse momento é o que evita falso
//          positivo com carga assíncrona: preenchimento vindo do backend antes
//          de o usuário encostar no formulário entra na foto como valor inicial;
//        - campo que só aparece depois (formulário condicional, wizard) entra
//          na foto quando recebe foco, com o valor que tinha na hora;
//        - no fechamento, compara foto x valor atual. Diferente = sujo.
//      O foco programático (`autoFocus`) de propósito NÃO tira a foto: só ação
//      real do usuário congela o ponto de partida.
//
// Para excluir um trecho da checagem, marque-o com `data-tx-sem-guarda`.

import { useRef, useMemo, useEffect, useContext, useCallback, createContext } from 'react';

// ----------------------------------------------------------------------

type Checador = () => boolean;

type GuardaContexto = {
  registrarChecador: (checador: Checador) => () => void;
};

export const TxModalGuardaContext = createContext<GuardaContexto | null>(null);

// Campos que carregam trabalho do usuário. `[contenteditable]` cobre o editor
// de texto rico, que não é `input` nem `textarea`.
const SELETOR_CAMPOS = 'input, textarea, select, [contenteditable="true"]';

// `hidden` e os `input` que são botão não guardam nada digitado.
const TIPOS_IGNORADOS = ['button', 'submit', 'reset', 'image', 'hidden'];

/** Lê o campo como texto comparável (checkbox vira 1/0, file vira a contagem). */
function lerValor(campo: Element): string {
  if (campo instanceof HTMLInputElement) {
    if (campo.type === 'checkbox' || campo.type === 'radio') return campo.checked ? '1' : '0';
    if (campo.type === 'file') return String(campo.files?.length ?? 0);
    return campo.value;
  }
  if (campo instanceof HTMLTextAreaElement || campo instanceof HTMLSelectElement) {
    return campo.value;
  }
  return (campo as HTMLElement).textContent ?? '';
}

function ehCampoRelevante(alvo: EventTarget | null): alvo is HTMLElement {
  if (!(alvo instanceof HTMLElement)) return false;
  if (!alvo.matches(SELETOR_CAMPOS)) return false;
  if (alvo.closest('[data-tx-sem-guarda]')) return false;
  if (alvo instanceof HTMLInputElement && TIPOS_IGNORADOS.includes(alvo.type)) return false;
  return true;
}

/** Foto de todos os campos da árvore, no estado em que estão agora. */
function fotografarCampos(raiz: HTMLElement): Map<Element, string> {
  const foto = new Map<Element, string>();
  Array.from(raiz.querySelectorAll(SELETOR_CAMPOS)).forEach((campo) => {
    if (ehCampoRelevante(campo)) foto.set(campo, lerValor(campo));
  });
  return foto;
}

// ----------------------------------------------------------------------

/**
 * Sinal explícito de sujeira, para quem quiser precisão em vez da heurística.
 *
 * Uso dentro de um formulário aberto por `abrirModal`:
 *
 *     const { formState } = useFormContext();
 *     useTxModalGuardaSaida(formState.isDirty);
 *
 * Fora de um modal global é um no-op, então pode ficar em componente
 * compartilhado sem quebrar as telas que o usam direto na página.
 */
export function useTxModalGuardaSaida(sujo: boolean | Checador): void {
  const contexto = useContext(TxModalGuardaContext);
  const sujoRef = useRef(sujo);

  useEffect(() => {
    sujoRef.current = sujo;
  }, [sujo]);

  useEffect(() => {
    if (!contexto) return undefined;
    return contexto.registrarChecador(() => {
      const atual = sujoRef.current;
      return typeof atual === 'function' ? atual() : atual;
    });
  }, [contexto]);
}

// ----------------------------------------------------------------------

type Guarda = {
  /**
   * Vai no elemento que embrulha o conteúdo do modal (o `DialogContent`).
   * É um ref de callback de propósito: o `Portal` do MUI só monta o conteúdo
   * do diálogo num segundo render, então um `useEffect` no `TxSimpleModal`
   * ainda encontraria o ref vazio.
   */
  conteudoRef: (no: HTMLDivElement | null) => void;
  /** `true` só quando há trabalho do usuário a perder. */
  estaSujo: () => boolean;
  /** Valor do contexto: embrulhe o conteúdo do modal com ele. */
  contexto: GuardaContexto;
};

/** Monta a guarda de um modal. Um por instância de `TxSimpleModal`. */
export function useGuardaDeSaida(): Guarda {
  const raizRef = useRef<HTMLElement | null>(null);
  const desligarRef = useRef<(() => void) | null>(null);
  // `null` = o usuário ainda não encostou em nada; sem ponto de partida não há
  // como estar sujo.
  const fotoRef = useRef<Map<Element, string> | null>(null);
  const checadoresRef = useRef<Set<Checador>>(new Set());

  const registrarChecador = useCallback((checador: Checador) => {
    checadoresRef.current.add(checador);
    return () => {
      checadoresRef.current.delete(checador);
    };
  }, []);

  const contexto = useMemo<GuardaContexto>(() => ({ registrarChecador }), [registrarChecador]);

  const conteudoRef = useCallback((no: HTMLDivElement | null) => {
    desligarRef.current?.();
    desligarRef.current = null;
    raizRef.current = no;
    if (!no) return;

    // Só ação real do usuário congela a foto — `autoFocus` não conta.
    const aoAgir = () => {
      if (!fotoRef.current) fotoRef.current = fotografarCampos(no);
    };

    // Campo que nasceu depois da foto (formulário condicional, passo de wizard)
    // entra com o valor que tem no momento em que o usuário vai mexer nele.
    const aoFocar = (evento: FocusEvent) => {
      const foto = fotoRef.current;
      if (!foto) return;
      const alvo = evento.target;
      if (ehCampoRelevante(alvo) && !foto.has(alvo)) foto.set(alvo, lerValor(alvo));
    };

    no.addEventListener('pointerdown', aoAgir, true);
    no.addEventListener('keydown', aoAgir, true);
    no.addEventListener('paste', aoAgir, true);
    no.addEventListener('focusin', aoFocar, true);

    desligarRef.current = () => {
      no.removeEventListener('pointerdown', aoAgir, true);
      no.removeEventListener('keydown', aoAgir, true);
      no.removeEventListener('paste', aoAgir, true);
      no.removeEventListener('focusin', aoFocar, true);
    };
  }, []);

  // Rede de segurança: se o modal sumir sem o ref ser chamado com `null`.
  useEffect(
    () => () => {
      desligarRef.current?.();
      desligarRef.current = null;
    },
    []
  );

  const estaSujo = useCallback(() => {
    // Sinal explícito manda: se alguém registrou `isDirty`, a heurística sai de
    // cena (senão um `reset()` pós-salvamento ainda pediria confirmação).
    const checadores = Array.from(checadoresRef.current);
    if (checadores.length > 0) {
      return checadores.some((checar) => {
        try {
          return checar();
        } catch {
          return false;
        }
      });
    }

    const raiz = raizRef.current;
    const foto = fotoRef.current;
    if (!raiz || !foto) return false;

    return Array.from(raiz.querySelectorAll(SELETOR_CAMPOS)).some((campo) => {
      const anterior = foto.get(campo);
      // Campo fora da foto nunca foi tocado pelo usuário: ignora, para não
      // confundir carga assíncrona com digitação.
      if (anterior === undefined) return false;
      return lerValor(campo) !== anterior;
    });
  }, []);

  return { conteudoRef, estaSujo, contexto };
}
